import { API_BASE_URL } from '../config/api';

/**
 * Rewrite relative /api/ paths in HTML to absolute URLs so images and links load.
 * - In dev (different port): API_BASE_URL is absolute (e.g. http://localhost:3005/api), so we rewrite.
 * - In production with same origin: API_BASE_URL is '/api', so we skip (relative URLs work).
 * - In production with separate API: set REACT_APP_API_BASE_URL to full URL and we rewrite.
 */
export function processNoteContent(content) {
  if (!content) return '';

  const isAbsoluteApi = typeof API_BASE_URL === 'string' && API_BASE_URL.startsWith('http');
  if (!isAbsoluteApi) {
    return content;
  }

  const base = API_BASE_URL.replace(/\/$/, '');
  const processed = content.replace(
    /(src|href)=["'](\/api\/[^"']*)["']/gi,
    (match, attr, path) => {
      const p = path.trim();
      if (p.startsWith('http://') || p.startsWith('https://')) {
        return match;
      }
      return `${attr}="${base}${p.replace(/^\/api/, '')}"`;
    }
  );

  return processed;
}

/**
 * Convert absolute API URLs back to relative /api/ paths.
 * Use when saving content from the editor so stored HTML uses /api/content/... (works in prod and dev).
 */
export function toRelativeApiPaths(content) {
  if (!content || process.env.NODE_ENV === 'production') return content || '';
  const base = API_BASE_URL.replace(/\/$/, '');
  const escaped = base.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return content.replace(
    new RegExp(`(src|href)=["']${escaped}(/content/[^"']*)["']`, 'gi'),
    (_, attr, path) => `${attr}="/api${path}"`
  );
}

/**
 * Tailwind prose-style classes for note body HTML (matches NoteDisplay).
 * Use with dangerouslySetInnerHTML wrapper: className={NOTE_PROSE_CLASSES}
 */
export const NOTE_PROSE_CLASSES =
  'prose prose-lg max-w-none ' +
  '[&_h1]:text-3xl [&_h1]:font-bold [&_h1]:text-gray-900 [&_h1]:mt-6 [&_h1]:mb-4 ' +
  '[&_h2]:text-2xl [&_h2]:font-bold [&_h2]:text-gray-800 [&_h2]:mt-5 [&_h2]:mb-3 ' +
  '[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:text-gray-800 [&_h3]:mt-4 [&_h3]:mb-2 ' +
  '[&_h4]:text-lg [&_h4]:font-semibold [&_h4]:text-gray-700 [&_h4]:mt-3 [&_h4]:mb-2 ' +
  '[&_h5]:text-base [&_h5]:font-semibold [&_h5]:text-gray-700 [&_h5]:mt-2 [&_h5]:mb-1 ' +
  '[&_h6]:text-sm [&_h6]:font-semibold [&_h6]:text-gray-700 [&_h6]:mt-2 [&_h6]:mb-1 ' +
  '[&_p]:mb-4 [&_p]:text-gray-700 [&_p]:leading-relaxed ' +
  '[&_a]:underline [&_a]:text-blue-600 [&_a:hover]:text-blue-800 [&_a:visited]:text-purple-600 ' +
  '[&_strong]:font-semibold [&_strong]:text-gray-900 [&_em]:italic ' +
  '[&_ul]:list-disc [&_ul]:ml-6 [&_ul]:mb-4 [&_ul]:space-y-1 ' +
  '[&_ol]:list-decimal [&_ol]:ml-6 [&_ol]:mb-4 [&_ol]:space-y-1 [&_li]:mb-1 ' +
  '[&_blockquote]:border-l-4 [&_blockquote]:border-gray-300 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-gray-600 [&_blockquote]:my-4 ' +
  '[&_code]:bg-gray-100 [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-sm [&_code]:font-mono ' +
  '[&_pre]:bg-gray-100 [&_pre]:p-4 [&_pre]:rounded [&_pre]:overflow-x-auto [&_pre]:my-4 ' +
  '[&_img]:max-w-full [&_img]:h-auto [&_img]:rounded [&_img]:my-4 ' +
  '[&_table]:w-full [&_table]:border-collapse [&_table]:my-4 ' +
  '[&_th]:border [&_th]:border-gray-300 [&_th]:px-4 [&_th]:py-2 [&_th]:bg-gray-100 [&_th]:font-semibold [&_th]:text-left ' +
  '[&_td]:border [&_td]:border-gray-300 [&_td]:px-4 [&_td]:py-2 [&_hr]:my-6 [&_hr]:border-gray-300';
